// frontend/src/components/BGNLiveSection.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Eye, ExternalLink, ChevronLeft, ChevronRight, Tv } from 'lucide-react';
import {
  sanitizeTwitchUsername,
  buildTwitchEmbedUrl,
  sanitizeImageUrl,
  sanitizeText,
  safeUrl,
  getParentDomain
} from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const formatViewers = (n) => {
  if (!n) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
};

const BGNLiveSection = () => {
  const [hero,      setHero]      = useState(null);
  const [secondary, setSecondary] = useState([]);
  const [scroll,    setScroll]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/featured-streamers`)
      .then(r => r.json())
      .then(data => {
        setHero(data.hero || null);
        setSecondary(data.secondary || []);
        setScroll(data.community || data.scroll || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const scrollLeft  = () => scrollRef.current?.scrollBy({ left: -320, behavior: 'smooth' });
  const scrollRight = () => scrollRef.current?.scrollBy({ left: 320, behavior: 'smooth' });

  const hasContent = hero || secondary.length > 0 || scroll.length > 0;
  if (!loading && !hasContent) return null;

  if (loading) return (
    <section className="section-padding bg-gray-50" id="live">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="h-8 w-40 bg-gray-200 animate-pulse mb-2" />
            <div className="h-4 w-64 bg-gray-100 animate-pulse" />
          </div>
        </div>
        {/* Hero skeleton */}
        <div className="mb-12 bg-white border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
            <div className="lg:col-span-2 bg-gray-200 animate-pulse" style={{ minHeight: 360 }} />
            <div className="p-8 space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                <div className="space-y-2">
                  <div className="h-4 w-32 bg-gray-200 animate-pulse" />
                  <div className="h-3 w-20 bg-gray-100 animate-pulse" />
                </div>
              </div>
              <div className="h-3 w-full bg-gray-100 animate-pulse" />
              <div className="h-3 w-3/4 bg-gray-100 animate-pulse" />
              <div className="h-10 w-full bg-gray-200 animate-pulse mt-auto" />
            </div>
          </div>
        </div>
        {/* Secondary skeletons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 overflow-hidden">
              <div className="aspect-video bg-gray-200 animate-pulse" />
              <div className="p-4 space-y-2">
                <div className="h-3 w-3/4 bg-gray-200 animate-pulse" />
                <div className="h-3 w-1/2 bg-gray-100 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  // Auto-promote: if hero is offline, use first live secondary instead
  let displayHero   = hero;
  let promotedLogin = null;

  if (hero && !hero?.is_live) {
    const liveSecondary = secondary.find(s => s.is_live);
    if (liveSecondary) {
      displayHero   = liveSecondary;
      promotedLogin = liveSecondary.login?.toLowerCase();
    }
  }

  const displaySecondary = promotedLogin
    ? secondary.filter(s => s.login?.toLowerCase() !== promotedLogin)
    : secondary;

  // Sanitize hero data
  const safeHeroLogin       = displayHero ? sanitizeTwitchUsername(displayHero.login) : null;
  const safeHeroDisplayName = displayHero ? sanitizeText(displayHero.display_name || displayHero.login) : null;
  const safeHeroAvatar      = displayHero ? sanitizeImageUrl(displayHero.avatar_url) : null;
  const heroEmbedUrl = safeHeroLogin ? buildTwitchEmbedUrl(safeHeroLogin, getParentDomain()) : null;
  const heroTwitchUrl = safeHeroLogin ? safeUrl(`https://twitch.tv/${safeHeroLogin}`) : null;

  return (
    <section className="section-padding bg-gray-50" id="live">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <h2 className="heading-large text-black lowercase mb-2">BGN Live</h2>
            <p className="body-large text-gray-600">Watch our community streaming live right now</p>
          </div>
          <Link
            to="/streamers"
            className="bg-black text-white px-6 py-3 text-sm font-medium uppercase tracking-wide hover:bg-gray-800 transition-colors duration-200 flex items-center space-x-2"
          >
            <span>View All</span>
            <ExternalLink size={14} />
          </Link>
        </div>

        {/* Hero Embed */}
        {displayHero && safeHeroLogin && (
          <div className="mb-12">
            <div className="bg-white border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">

                {/* Embed or placeholder */}
                <div className="lg:col-span-2 relative bg-black" style={{ minHeight: 360 }}>
                  {displayHero?.is_live && heroEmbedUrl ? (
                    <iframe
                      src={heroEmbedUrl}
                      height="100%"
                      width="100%"
                      allowFullScreen
                      sandbox="allow-scripts allow-same-origin allow-presentation"
                      style={{ minHeight: 360, display: 'block', border: 'none' }}
                      title={`${safeHeroDisplayName} stream`}
                    />
                  ) : safeHeroAvatar ? (
                    <div className="absolute inset-0">
                      <img src={safeHeroAvatar} alt={safeHeroDisplayName} className="w-full h-full object-cover object-top opacity-60" />
                      <div className="absolute inset-0 flex flex-col items-end justify-end p-4 bg-gradient-to-t from-black/70 to-transparent">
                        <p className="text-white text-xs uppercase tracking-wider">Currently Offline</p>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
                      <Tv size={48} className="text-gray-600 mb-4" />
                      <p className="text-gray-500 text-sm uppercase tracking-wider mb-1">Currently Offline</p>
                      <p className="text-gray-600 text-xs">{safeHeroDisplayName}</p>
                    </div>
                  )}
                </div>

                {/* Info panel */}
                <div className="p-8 flex flex-col justify-between bg-white">
                  <div>
                    <div className="flex items-center space-x-3 mb-4">
                      {safeHeroAvatar && (
                        <img
                          src={safeHeroAvatar}
                          alt={safeHeroDisplayName}
                          className="w-12 h-12 rounded-full border border-gray-200"
                          onError={(e) => { e.target.src = '/images/default-avatar.png'; }}
                        />
                      )}
                      <div>
                        <h3 className="heading-small text-black">{safeHeroDisplayName}</h3>
                        {displayHero?.is_live && (
                          <div className="flex items-center space-x-1 mt-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            <span className="text-red-600 text-xs font-medium uppercase tracking-wide">Live</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {displayHero?.stream_title && (
                      <p className="body-small text-gray-600 mb-3 leading-relaxed">
                        {sanitizeText(displayHero.stream_title)}
                      </p>
                    )}
                    {displayHero?.game_name && (
                      <span className="inline-block bg-gray-100 text-gray-700 px-3 py-1 text-xs font-medium mb-4">
                        {sanitizeText(displayHero.game_name)}
                      </span>
                    )}
                    {displayHero?.is_live && displayHero?.viewer_count > 0 && (
                      <div className="flex items-center space-x-1 text-gray-500 text-sm mb-4">
                        <Eye size={14} />
                        <span>{formatViewers(displayHero.viewer_count)} watching</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {displayHero?.is_bgn_member && safeHeroLogin && (
                      <Link
                        to={`/profile/${safeHeroLogin}`}
                        className="bg-yellow-400 text-black px-6 py-3 font-medium hover:bg-yellow-300 transition-colors duration-200 flex items-center justify-center space-x-2 text-sm uppercase tracking-wide w-full"
                      >
                        <span>Visit Profile</span>
                      </Link>
                    )}
                    {heroTwitchUrl && (
                      <a
                        href={heroTwitchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-black text-white px-6 py-3 font-medium hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center space-x-2 text-sm uppercase tracking-wide w-full"
                      >
                        <ExternalLink size={14} />
                        <span>Watch on Twitch</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Secondary Cards */}
        {displaySecondary.length > 0 && (
          <div className="mb-12">
            <h3 className="heading-small text-black mb-6">Featured Streamers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {displaySecondary.map(streamer => (
                <SecondaryCard key={streamer.id} streamer={streamer} liveData={streamer} />
              ))}
            </div>
          </div>
        )}

        {/* Scroll List */}
        {scroll.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="heading-small text-black">BGN Community</h3>
              <div className="flex space-x-2">
                <button onClick={scrollLeft}  className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-black transition-colors duration-200"><ChevronLeft size={18} /></button>
                <button onClick={scrollRight} className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-black transition-colors duration-200"><ChevronRight size={18} /></button>
              </div>
            </div>
            <div ref={scrollRef} className="flex space-x-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {scroll.map(streamer => (
                <ScrollCard key={streamer.id} streamer={streamer} />
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
};


// ── Secondary card — fetches live status ────────────────────────────────────

const SecondaryCard = ({ streamer, liveData: propLiveData }) => {
  const [liveData,    setLiveData]    = useState(propLiveData || null);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    if (propLiveData !== undefined) setLiveData(propLiveData);
  }, [propLiveData]);

  const safeLogin       = sanitizeTwitchUsername(streamer.login);
  const safeDisplayName = sanitizeText(streamer.display_name || streamer.login);
  const safeAvatar      = sanitizeImageUrl(streamer.avatar_url);
  const safeThumbnail   = liveData?.thumbnail_url ? sanitizeImageUrl(liveData.thumbnail_url) : null;
  const twitchUrl       = safeLogin ? safeUrl(`https://twitch.tv/${safeLogin}`) : null;
  const isBgnMember     = streamer.is_bgn_member || liveData?.is_bgn_member;
  const profileUrl      = isBgnMember ? `/profile/${safeLogin}` : null;

  if (!twitchUrl) return null;

  const isLive       = liveData?.is_live;
  const showThumb    = isLive && safeThumbnail && !thumbFailed;
  const showAvatar   = !showThumb && safeAvatar && !avatarFailed;
  const showFallback = !showThumb && !showAvatar;

  const CardWrapper = ({ children }) => profileUrl
    ? <Link to={profileUrl} className="bg-white border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer group block">{children}</Link>
    : <a href={twitchUrl} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer group block">{children}</a>;

  return (
    <CardWrapper>
      <div className="relative aspect-video bg-gray-900">
        {/* Live thumbnail */}
        {showThumb && (
          <img
            src={safeUrl(safeThumbnail)}
            alt={safeDisplayName}
            className="w-full h-full object-cover"
            onError={() => setThumbFailed(true)}
          />
        )}
        {/* Offline — show profile picture */}
        {showAvatar && (
          <img
            src={safeAvatar}
            alt={safeDisplayName}
            className="w-full h-full object-cover opacity-80"
            onError={() => setAvatarFailed(true)}
          />
        )}
        {/* Last resort — TV icon */}
        {showFallback && (
          <div className="w-full h-full flex items-center justify-center">
            <Tv size={24} className="text-gray-600" />
          </div>
        )}

        {/* Offline overlay label */}
        {!isLive && (showAvatar || showFallback) && (
          <div className="absolute inset-0 bg-black/40 flex items-end p-2">
            <span className="text-white/60 text-xs uppercase tracking-wide">Offline</span>
          </div>
        )}

        {isLive && (
          <div className="absolute top-2 left-2">
            <span className="bg-red-600 text-white px-2 py-0.5 text-xs font-medium uppercase tracking-wide">Live</span>
          </div>
        )}
        {isLive && liveData?.viewer_count > 0 && (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-0.5 text-xs flex items-center space-x-1">
            <Eye size={10} />
            <span>{formatViewers(liveData.viewer_count)}</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center space-x-2 mb-1">
          {safeAvatar && !avatarFailed && (
            <img
              src={safeAvatar}
              alt={safeDisplayName}
              className="w-6 h-6 rounded-full flex-shrink-0"
              onError={() => setAvatarFailed(true)}
            />
          )}
          <h4 className="text-black font-medium text-sm truncate">{safeDisplayName}</h4>
        </div>
        {liveData?.game_name && (
          <p className="text-gray-500 text-xs truncate">{sanitizeText(liveData.game_name)}</p>
        )}
        {!isLive && (
          <p className="text-gray-400 text-xs">Offline</p>
        )}
      </div>
    </CardWrapper>
  );
};


// ── Scroll card ─────────────────────────────────────────────────────────────

const ScrollCard = ({ streamer }) => {
  const [imgFailed, setImgFailed] = useState(false);

  const safeLogin = sanitizeTwitchUsername(streamer.login);
  const safeDisplayName = sanitizeText(streamer.display_name || streamer.login);
  const safeAvatar = sanitizeImageUrl(streamer.avatar_url);

  if (!safeLogin) return null;

  const displayInitial = safeDisplayName[0]?.toUpperCase() || '?';
  const showImage = safeAvatar && !imgFailed;

  return (
    <Link
      to={`/profile/${safeLogin}`}
      className="flex-shrink-0 w-40 bg-white border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer block"
    >
      <div className="relative aspect-square bg-gray-200">
        {showImage ? (
          <img
            src={safeAvatar}
            alt={safeDisplayName}
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-gray-400 text-2xl font-bold">{displayInitial}</span>
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-black text-xs font-medium truncate">{safeDisplayName}</p>
      </div>
    </Link>
  );
};


export default BGNLiveSection;